import { faArrowDown, faCircleInfo, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import { FormEvent, use, useCallback, useEffect, useState } from 'react';
import { Button, Col, Collapse, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import useAlert from '../../../Alerts/useAlert';
import CentredSpinner from '../../../Components/CentredSpinner';
import DateTimePicker from '../../../Components/DateTimePickerV2';
import MultiselectBox from '../../../Components/MultiselectBox';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import { UserPreferencesContext } from '../../../Contexts/UserPreferencesContext';
import CommentDialog from '../../../Dialogs/CommentDialog';
import { Quality } from '../../../Types/LotDetailQuality';
import WithComment from '../../../Types/WithComment';
import { convertUTCToLocalTime, useDateUtils } from '../../../Utilities/useDateUtils';
import { useHttpUtils } from '../../../Utilities/useHttpUtils';
import { LotSplitTypes } from './LotSplitPackageDialog';

interface LotSplitForQualityProps {
    showDialog: boolean;
    closeDialog: () => void;
    alternativeGrades: string[];
    preferredLotBDigit?: number | null;
}

interface QualityLotSplitPoint {
    id: number;
    lotNumber: string,
    buildStartTimeUTC: DateTime | null,
    buildEndTimeUTC: DateTime | null;
    grade: string,
}

function LotSplitForQuality({ showDialog, closeDialog, alternativeGrades, preferredLotBDigit = null }: LotSplitForQualityProps) {
    var {
        lotDetail: {
            summary: {
                lotNumber,
                buildStartUtc,
                buildEndUtc,
                grade: originGrade,
            },
            qualities,
            relatedLots
        },
        lotFlags: {
            recalcAllQualitiesOnQualitySplit
        }
    } = use(LotDetailContext);
    const { t } = useTranslation();
    const { convertUTCToShortDateTime } = useDateUtils();

    const addNewLotSplit = useCallback((newLots: QualityLotSplitPoint[]) => {
        const id = Math.floor(Math.random() * 10000000);
        var initialLotNumber: string;

        if (preferredLotBDigit === null) {
            initialLotNumber = lotNumber;
        } else if (preferredLotBDigit === 0) {
            initialLotNumber = `${lotNumber.slice(0, -3)}${+lotNumber.slice(-3, -2) + 1}0${lotNumber.slice(-1) + newLots.length}`;
        } else {
            initialLotNumber = `${lotNumber.slice(0, -2)}${preferredLotBDigit}${lotNumber.slice(-1) + newLots.length}`;
        }

        const initialBuildEndTime = buildEndUtc;

        let initialBuildStartTime: DateTime | null = newLots.length === 0
            ? buildStartUtc
            : newLots[newLots.length - 1].buildEndTimeUTC?.plus({ minute: 1 }) ?? null;

        return [...newLots,
        {
            id: id,
            lotNumber: initialLotNumber,
            buildStartTimeUTC: initialBuildStartTime,
            buildEndTimeUTC: initialBuildEndTime,
            grade: originGrade
        }];
    }, [buildEndUtc, buildStartUtc, lotNumber, originGrade]);

    const [newLots, setNewLots] = useState<QualityLotSplitPoint[]>(addNewLotSplit([]));

    const [selectedQualities, setSelectedQualities] = useState<Quality[]>([]);
    const [sourceLotGrade, setSourceLotGrade] = useState<string>();

    useEffect(() => {
        if (relatedLots.some(l => l.quality)) {
            let qualitySplitLots = relatedLots.filter(l => l.quality && l.lotNumber !== lotNumber);
            setNewLots(qualitySplitLots.map(l => ({
                id: Math.floor(Math.random() * 10000000),
                lotNumber: l.lotNumber,
                grade: l.grade,
                buildStartTimeUTC: l.lotStartUtc,
                buildEndTimeUTC: l.lotEndUtc
            })));

            let splitQualities = qualitySplitLots[0].quality.split(",").map(x => x.trim());
            setSelectedQualities(qualities?.filter(q => splitQualities.includes(q.name)) ?? []);
        }
    }, [lotNumber, qualities, relatedLots]);

    useEffect(() => {
        setSourceLotGrade(originGrade);
    }, [originGrade]);

    const addNewLot = useCallback(() => {
        setNewLots(newLots => addNewLotSplit(newLots));
    }, [addNewLotSplit]);

    const updateLotNumber = (id: number, lotNumber: string) => {
        setNewLots(newLots => [...newLots.map(l => l.id === id ? { ...l, lotNumber: lotNumber } : l)]);
    };

    const updateLotStart = (id: number, lotStart: DateTime | null) => {
        setNewLots(newLots => [...newLots.map(l => l.id === id
            ? {
                ...l,
                buildStartTimeUTC: lotStart
            } : l)]);
    };

    const updateLotEnd = (id: number, lotEnd: DateTime | null) => {
        setNewLots(newLots => [...newLots.map(l => l.id === id
            ? {
                ...l,
                buildEndTimeUTC: lotEnd
            } : l)]);
    };

    const updateLotGrade = (id: number, grade: string) => {
        if (id === 0) {
            setSourceLotGrade(grade);
        } else {
            setNewLots(newLots => [...newLots.map(l => l.id === id ? { ...l, grade: grade } : l)]);
        }
    };

    const removeLot = (id: number) => {
        setNewLots(newLots => [...newLots.filter(l => l.id !== id)]);
    };

    const [validated, setValidated] = useState(false);
    const [formErrors, setFormErrors] = useState<Partial<any>>({});
    const [showCommentDialog, setShowCommentDialog] = useState(false);

    const isEmpty = (obj: object): boolean => {
        return Object.keys(obj).length === 0;
    };

    const submitForm = (e: FormEvent<HTMLFormElement>) => {
        e.stopPropagation();
        e.preventDefault();

        let formErrors: any = {};

        if (!recalcAllQualitiesOnQualitySplit && selectedQualities.length === 0) {
            formErrors["proxyQualities"] = "At least one quality needs to be selected";
        }

        // Validation
        //  1. split lots cannot start before the origin lot
        //  2. split lots cannot end after the origin lot
        //  3. split lots cannot overlap

        const originalStart = convertUTCToLocalTime(buildStartUtc);
        const originalEnd = convertUTCToLocalTime(buildEndUtc);

        for (let i = 0; i < newLots.length; i++) {
            if (newLots[i].lotNumber === lotNumber) {
                formErrors[newLots[i].id] = { lotNumber: "Lot number must be unique" };
            }

            if (newLots[i].buildStartTimeUTC === null) {
                formErrors[newLots[i].id] = { buildStart: "Lot start must be provided" };
                continue;
            }

            if (newLots[i].buildEndTimeUTC === null) {
                formErrors[newLots[i].id] = { buildEnd: "Lot end must be provided" };
                continue;
            }

            if (newLots[i].buildStartTimeUTC! > newLots[i].buildEndTimeUTC!) {
                formErrors[newLots[i].id] = { buildStart: "Start time cannot be before the end time" };
                continue;
            }

            if (newLots[i].buildStartTimeUTC! < originalStart) {
                formErrors[newLots[i].id] = { buildStart: "Lot start cannot be before the original lot's start time" };
            }

            if (newLots[i].buildEndTimeUTC! > originalEnd) {
                formErrors[newLots[i].id] = { buildEnd: "Lot end cannot be after the original lot's end time" };
            }

            for (let j = 0; j < newLots.length; j++) {
                if (i === j || newLots[j].buildStartTimeUTC === null || newLots[j].buildEndTimeUTC === null) {
                    continue;
                }

                if (newLots[i].lotNumber === newLots[j].lotNumber) {
                    formErrors[newLots[i].id] = { lotNumber: "Lot number must be unique" };
                }

                if (newLots[i].buildStartTimeUTC! <= newLots[j].buildEndTimeUTC! && newLots[i].buildEndTimeUTC! >= newLots[j].buildStartTimeUTC!) {
                    formErrors[newLots[i].id] = { buildStart: "Split lot times must not overlap" };
                }
            }
        }

        if (isEmpty(formErrors)) {
            setFormErrors([]);
            setShowCommentDialog(true);
        } else {
            setFormErrors(formErrors);
        }

        setValidated(true);
    };

    const closeDialogAndResetForm = () => {
        setNewLots([]);
        closeDialog();
    };

    const navigate = useNavigate();
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const [loading, setLoading] = useState(false);
    const saveSplitLots = (comment: string) => {
        // Build some object
        // Original lot to new lots

        // WithComment<>
        // Note, added original lot as first split so we can indicate the grade after the split
        let requestBody: WithComment<any[]> = {
            comment: comment,
            data: [
                {
                    lotNumber: lotNumber,
                    grade: sourceLotGrade,
                    qualities: recalcAllQualitiesOnQualitySplit ? qualities : selectedQualities
                },
                ...newLots.map(x => ({
                    lotNumber: x.lotNumber,
                    startTimeUtc: x.buildStartTimeUTC?.toISO(),
                    endTimeUtc: x.buildEndTimeUTC?.toISO(),
                    grade: x.grade,
                    qualities: recalcAllQualitiesOnQualitySplit ? qualities : selectedQualities
                }))
            ]
        };

        setLoading(true);
        return fetchDataAuthenticated(`api/v1/lot/${lotNumber}/splitForQuality`, "POST", JSON.stringify(requestBody))
            .then(async (response: any) => {
                if (response.ok) {
                    setAlert("success", "Success", t("LotSplit.Successful"));
                    closeDialogAndResetForm();
                    navigate('/lots');
                } else {
                    let errorMessage = await response.text();
                    throw new Error(`${t("LotSplit.Failed")} ${errorMessage}`);
                }
            })
            .catch((error: any) => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => setLoading(false));
    };

    const { userPreferences, saveUserPreference } = use(UserPreferencesContext);
    const [showHelpText, setShowHelpText] = useState(false);

    useEffect(() => {
        setShowHelpText(!!userPreferences?.lotDetail?.showLotSplitHelpText);
    }, [userPreferences?.lotDetail?.showLotSplitHelpText]);

    const toggleShowHelpText = () => {
        const newValue = !showHelpText;
        setShowHelpText(newValue);
        saveUserPreference("lotDetail.showLotSplitHelpText", newValue);
    };

    return (
        <>
            <Modal show={showDialog} size="xl" onHide={closeDialog} onExit={closeDialog} backdrop="static">
                <Modal.Header closeButton>
                    <div className="d-flex w-100">
                        <Modal.Title>{t<string>(`LotSplit.AltTitles.${LotSplitTypes[LotSplitTypes.Quality]}`, { lotNumber: lotNumber })}</Modal.Title>
                        <Button variant={"outline-primary"} className="rounded-circle p-1 ms-auto me-3" onClick={toggleShowHelpText} >
                            <FontAwesomeIcon size="2xl" icon={faCircleInfo} />
                        </Button>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {loading
                        ? <CentredSpinner />
                        : <>
                            <Collapse in={showHelpText}>
                                <div id="package-split-help-text">
                                    {((t(`LotSplit.HelpText.${LotSplitTypes[LotSplitTypes.QualityMultiple]}`, { returnObjects: true }) as string[]) ?? [])
                                        .map((t, index) => <div key={`package-split-help-text-${index}`}>{t}</div>)
                                    }
                                    <hr />
                                </div>
                            </Collapse>

                            <Form id="multi-split-form" onSubmit={(e) => submitForm(e)} noValidate validated={validated}>
                                <Row>
                                    <Col>
                                        <Form.Label>{t<string>("LotSplit.LotNumber")}:</Form.Label>
                                    </Col>
                                    <Col>
                                        <Form.Label>{t<string>("LotSplit.StartTime")}:</Form.Label>
                                    </Col>
                                    <Col>
                                        <Form.Label>{t<string>("LotSplit.EndTime")}:</Form.Label>
                                    </Col>
                                    <Col>
                                        <Form.Label>{t<string>("LotSplit.Grade")}:</Form.Label>
                                    </Col>
                                </Row>
                                <Row className="mb-1">
                                    <Col>
                                        <Form.Control value={lotNumber} readOnly />
                                    </Col>
                                    <Col>
                                        <Form.Control name="originalStart" value={convertUTCToShortDateTime(buildStartUtc ?? "")} readOnly />
                                    </Col>
                                    <Col>
                                        <Form.Control name="originalEnd" value={convertUTCToShortDateTime(buildEndUtc ?? "")} readOnly />
                                    </Col>
                                    <Col>
                                        <Form.Select
                                            aria-label="Select grade"
                                            name="sourceLotGrade"
                                            required
                                            value={sourceLotGrade}
                                            onChange={(e) => updateLotGrade(0, e.target.value)}>
                                            {!(alternativeGrades.includes(originGrade)) && <option value={originGrade}>{originGrade}</option>}
                                            {alternativeGrades.map((grade, index) =>
                                                <option key={`altgrade-option-${index}`} value={grade}>{grade}</option>
                                            )}
                                        </Form.Select>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col className="d-flex justify-content-center">
                                        <FontAwesomeIcon size="xl" icon={faArrowDown} />
                                    </Col>
                                </Row>
                                {
                                    !recalcAllQualitiesOnQualitySplit &&
                                    <Row className="mb-1">
                                        <Col>
                                            <MultiselectBox<Quality>
                                                id={"proxyQualities"}
                                                name={t("LotSplit.Qualities")}
                                                options={qualities}
                                                selectedOptions={selectedQualities}
                                                setSelectedOptions={setSelectedQualities}
                                                map="name"
                                                errors={formErrors} />
                                        </Col>
                                    </Row>
                                }
                                <Row>
                                    <Col>
                                        <Row className="mb-1">
                                            <Col sm={3}><span>{t<string>("LotSplit.LotNumber")}</span></Col>
                                            <Col sm={3}><span>{t<string>("LotSplit.StartTime")}</span></Col>
                                            <Col sm={3}><span>{t<string>("LotSplit.EndTime")}</span></Col>
                                            <Col sm={2}><span>{t<string>("LotSplit.Grade")}</span></Col>
                                            <Col sm={1}><span></span></Col>
                                        </Row>
                                        {newLots.map((lot) =>
                                            <Row key={`lot-split-multiple-lots-row-${lot.id}`} className="mb-1">
                                                <Col sm={3}>
                                                    <Form.Group as={Col} controlId={`lot-${lot.id}-lotNumber`}>
                                                        <Form.Control value={lot.lotNumber} onChange={(e) => updateLotNumber(lot.id, e.target.value)} isInvalid={formErrors[lot.id]?.lotNumber} />
                                                        <Form.Text className="text-danger">{formErrors[lot.id]?.lotNumber}</Form.Text>
                                                    </Form.Group>
                                                </Col>
                                                <Col sm={3}>
                                                    <Form.Group as={Col} controlId={`lot-${lot.id}-buildStart`}>
                                                        <DateTimePicker name="splitStart" required dateTimeUTC={lot.buildStartTimeUTC} handleDateSelect={(date) => updateLotStart(lot.id, date)} isInvalid={!!formErrors[lot.id]?.buildStart} />
                                                        <Form.Text className="text-danger"> {formErrors[lot.id]?.buildStart} </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                                <Col sm={3}>
                                                    <Form.Group as={Col} controlId={`lot-${lot.id}-buildEnd`} v>
                                                        <DateTimePicker name="splitEnd" required dateTimeUTC={lot.buildEndTimeUTC} handleDateSelect={(date) => updateLotEnd(lot.id, date)} isInvalid={!!formErrors[lot.id]?.buildEnd} />
                                                        <Form.Text className="text-danger"> {formErrors[lot.id]?.buildEnd} </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                                <Col sm={2}>
                                                    <Form.Group as={Col} controlId={`lot-${lot.id}-grade`} v>
                                                        <Form.Select
                                                            aria-label="Select grade"
                                                            name="grade"
                                                            required
                                                            value={lot.grade}
                                                            onChange={(e) => updateLotGrade(lot.id, e.target.value)}>
                                                            {!(alternativeGrades.includes(originGrade)) && <option value={originGrade}>{originGrade}</option>}
                                                            {alternativeGrades.map((grade, index) =>
                                                                <option key={`altgrade-option-${index}`} value={grade}>{grade}</option>
                                                            )}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                <Col sm={1} className="p-0">
                                                    <Button onClick={() => removeLot(lot.id)} variant="danger">
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </Button>
                                                </Col>
                                            </Row>)}
                                        <Row>
                                            <Col>
                                                <Button onClick={addNewLot}>{t<string>("LotSplit.AddLotRow")}</Button>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </Form>
                        </>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeDialog} className="me-2">
                        {t<string>("Application.Controls.Cancel")}
                    </Button>
                    <Button variant="primary" type="submit" form="multi-split-form" name="confirmSplit">
                        {t<string>("Application.Controls.Save")}
                    </Button>
                </Modal.Footer>
            </Modal>
            <CommentDialog showDialog={showCommentDialog} closeDialog={() => setShowCommentDialog(false)} onSave={saveSplitLots} />
        </>
    );
}

export default LotSplitForQuality;